/**
 * Curated fields for the visual admin (“customiser”).
 * Paths: translation = under payload[lang]; contact = contactInfo; servicePage = servicePages[lang][slug].
 */

/** @typedef {'text' | 'textarea' | 'image'} FieldType */

/**
 * @typedef {Object} Field
 * @property {string} path — dot path under section domain
 * @property {string} label
 * @property {FieldType} [type]
 */

/**
 * @typedef {Object} Section
 * @property {string} id
 * @property {string} title
 * @property {string} [subtitle] — where it appears on site
 * @property {'translation' | 'contact' | 'servicePage'} domain
 * @property {string} [slug] — for servicePage
 * @property {Field[]} fields
 */

/** @type {Section[]} */
export const CUSTOMIZER_SECTIONS = [
  {
    id: "nav",
    title: "Meniu principal",
    subtitle: "Header — toate paginile",
    domain: "translation",
    fields: [
      { path: "nav.home", label: "Acasă / Home" },
      { path: "nav.services", label: "Servicii" },
      { path: "nav.portfolio", label: "Portofoliu" },
      { path: "nav.about", label: "Despre" },
      { path: "nav.contact", label: "Contact" },
      { path: "nav.cta", label: "Buton CTA" },
    ],
  },
  {
    id: "hero",
    title: "Pagina principală — Hero",
    subtitle: "Secțiunea de sus (/)",
    domain: "translation",
    fields: [
      { path: "hero.eyebrow", label: "Eyebrow" },
      { path: "hero.title_line1", label: "Titlu linia 1" },
      { path: "hero.title_line2", label: "Titlu linia 2" },
      { path: "hero.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "hero.cta_primary", label: "Buton principal" },
      { path: "hero.cta_secondary", label: "Buton secundar" },
      { path: "hero.scroll", label: "Text scroll" },
    ],
  },
  {
    id: "stats",
    title: "Pagina principală — Statistici",
    subtitle: "Bloc cifre (/)",
    domain: "translation",
    fields: [
      { path: "stats.title", label: "Etichetă scurtă (badge)" },
      { path: "stats.subtitle", label: "Titlu principal" },
      { path: "stats.description", label: "Paragraf descriere", type: "textarea" },
      { path: "stats.items.0.value", label: "Card 1 — valoare" },
      { path: "stats.items.0.label", label: "Card 1 — etichetă" },
      { path: "stats.items.1.value", label: "Card 2 — valoare" },
      { path: "stats.items.1.label", label: "Card 2 — etichetă" },
      { path: "stats.items.2.value", label: "Card 3 — valoare" },
      { path: "stats.items.2.label", label: "Card 3 — etichetă" },
      { path: "stats.items.3.value", label: "Card 4 — valoare" },
      { path: "stats.items.3.label", label: "Card 4 — etichetă" },
    ],
  },
  {
    id: "home-services",
    title: "Pagina principală — Servicii (rezumat)",
    subtitle: "Două carduri pe home",
    domain: "translation",
    fields: [
      { path: "services.eyebrow", label: "Eyebrow" },
      { path: "services.title", label: "Titlu" },
      { path: "services.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "services.items.0.title", label: "Card 1 — titlu" },
      { path: "services.items.0.description", label: "Card 1 — descriere", type: "textarea" },
      { path: "services.items.1.title", label: "Card 2 — titlu" },
      { path: "services.items.1.description", label: "Card 2 — descriere", type: "textarea" },
      { path: "services.cta", label: "Link „vezi toate”" },
    ],
  },
  {
    id: "process",
    title: "Pagina principală — Proces",
    subtitle: "4 pași",
    domain: "translation",
    fields: [
      { path: "process.eyebrow", label: "Eyebrow" },
      { path: "process.title", label: "Titlu" },
      { path: "process.steps.0.title", label: "Pas 1 — titlu" },
      { path: "process.steps.0.desc", label: "Pas 1 — descriere", type: "textarea" },
      { path: "process.steps.1.title", label: "Pas 2 — titlu" },
      { path: "process.steps.1.desc", label: "Pas 2 — descriere", type: "textarea" },
      { path: "process.steps.2.title", label: "Pas 3 — titlu" },
      { path: "process.steps.2.desc", label: "Pas 3 — descriere", type: "textarea" },
      { path: "process.steps.3.title", label: "Pas 4 — titlu" },
      { path: "process.steps.3.desc", label: "Pas 4 — descriere", type: "textarea" },
    ],
  },
  {
    id: "testimonials",
    title: "Pagina principală — Testimoniale (antet)",
    subtitle: "Titluri deasupra listei",
    domain: "translation",
    fields: [
      { path: "testimonials.eyebrow", label: "Eyebrow" },
      { path: "testimonials.title", label: "Titlu" },
    ],
  },
  {
    id: "cta-home",
    title: "Pagina principală — CTA final",
    subtitle: "Înainte de footer pe home",
    domain: "translation",
    fields: [
      { path: "cta_section.eyebrow", label: "Eyebrow" },
      { path: "cta_section.title", label: "Titlu" },
      { path: "cta_section.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "cta_section.button", label: "Text buton" },
    ],
  },
  {
    id: "trusted",
    title: "Pagina principală — Bandă „încredere”",
    subtitle: "Text sub secțiuni",
    domain: "translation",
    fields: [{ path: "trusted", label: "Text bandă" }],
  },
  {
    id: "footer",
    title: "Footer",
    subtitle: "Toate paginile",
    domain: "translation",
    fields: [
      { path: "footer.tagline", label: "Tagline" },
      { path: "footer.explore", label: "„Explorează”" },
      { path: "footer.services_title", label: "Titlu coloană servicii" },
      { path: "footer.contact_title", label: "Titlu coloană contact" },
      { path: "footer.rights", label: "Drepturi de autor" },
      { path: "footer.made_with", label: "Made with / locație" },
    ],
  },
  {
    id: "about",
    title: "Pagina Despre",
    subtitle: "/about",
    domain: "translation",
    fields: [
      { path: "about.eyebrow", label: "Eyebrow" },
      { path: "about.title", label: "Titlu principal" },
      { path: "about.lead", label: "Lead", type: "textarea" },
      { path: "about.p1", label: "Paragraf 1", type: "textarea" },
      { path: "about.p2", label: "Paragraf 2", type: "textarea" },
      { path: "about.team_name", label: "Nume echipă / fondator" },
      { path: "about.team_role", label: "Rol" },
      { path: "about.team_bio", label: "Bio", type: "textarea" },
      { path: "about.section_image_title", label: "Secțiune imagine — titlu" },
      { path: "about.section_image_body", label: "Secțiune imagine — text", type: "textarea" },
    ],
  },
  {
    id: "services-page",
    title: "Pagina Servicii (overview)",
    subtitle: "/services",
    domain: "translation",
    fields: [
      { path: "services_page.eyebrow", label: "Eyebrow" },
      { path: "services_page.title", label: "Titlu" },
      { path: "services_page.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "services_page.cta_title", label: "CTA — titlu" },
      { path: "services_page.cta_subtitle", label: "CTA — subtitlu", type: "textarea" },
      { path: "services_page.cta_button", label: "CTA — buton" },
    ],
  },
  {
    id: "portfolio",
    title: "Pagina / secțiune Portofoliu",
    subtitle: "Home + /portfolio",
    domain: "translation",
    fields: [
      { path: "portfolio.eyebrow", label: "Eyebrow" },
      { path: "portfolio.title", label: "Titlu" },
      { path: "portfolio.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "portfolio.cta", label: "Buton CTA" },
      { path: "portfolio.items.0.name", label: "Proiect 1 — nume" },
      { path: "portfolio.items.0.service", label: "Proiect 1 — serviciu" },
      { path: "portfolio.items.0.image", label: "Proiect 1 — imagine URL", type: "image" },
      { path: "portfolio.items.1.name", label: "Proiect 2 — nume" },
      { path: "portfolio.items.1.service", label: "Proiect 2 — serviciu" },
      { path: "portfolio.items.1.image", label: "Proiect 2 — imagine URL", type: "image" },
      { path: "portfolio.items.2.name", label: "Proiect 3 — nume" },
      { path: "portfolio.items.2.service", label: "Proiect 3 — serviciu" },
      { path: "portfolio.items.2.image", label: "Proiect 3 — imagine URL", type: "image" },
    ],
  },
  {
    id: "contact-page",
    title: "Pagina Contact",
    subtitle: "/contact",
    domain: "translation",
    fields: [
      { path: "contact.eyebrow", label: "Eyebrow" },
      { path: "contact.title", label: "Titlu" },
      { path: "contact.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "contact.form.name", label: "Form — label nume" },
      { path: "contact.form.email", label: "Form — label email" },
      { path: "contact.form.phone", label: "Form — label telefon" },
      { path: "contact.form.message", label: "Form — label mesaj" },
      { path: "contact.form.submit", label: "Form — trimite" },
      { path: "contact.form.success", label: "Form — mesaj succes" },
      { path: "contact.info_title", label: "Titlu „alte căi”" },
      { path: "contact.location_value", label: "Valoare locație" },
      { path: "contact.hours_value", label: "Valoare program" },
    ],
  },
  {
    id: "contact-info",
    title: "Date contact (globale)",
    subtitle: "Telefon, email în header / floating / footer",
    domain: "contact",
    fields: [
      { path: "email", label: "Email" },
      { path: "phone", label: "Telefon (afișat)" },
      { path: "instagram", label: "Instagram (@)" },
      { path: "footerLogoUrl", label: "Logo footer (URL), opțional" },
      { path: "socialInstagramUrl", label: "URL Instagram (footer)" },
      { path: "socialFacebookUrl", label: "URL Facebook" },
      { path: "socialTiktokUrl", label: "URL TikTok" },
      { path: "socialLinkedinUrl", label: "URL LinkedIn" },
    ],
  },
  ...["social-media-management", "ads", "website", "consultanta"].map((slug) => {
    const svcTitles = {
      "social-media-management": "Social Media Management",
      ads: "Campanii Ads",
      website: "Websites",
      consultanta: "Consultanță",
    };
    return {
      id: `svc-${slug}`,
      title: `Serviciu: ${svcTitles[slug] || slug}`,
      subtitle: `/services/${slug}`,
      domain: "servicePage",
      slug,
      fields: [
        { path: "intro_image", label: "Imagine header", type: "image" },
        { path: "title", label: "Titlu principal" },
        { path: "lead", label: "Lead", type: "textarea" },
        { path: "intro_title", label: "Intro — titlu" },
        { path: "intro_body", label: "Intro — text", type: "textarea" },
        { path: "cta_title", label: "CTA — titlu" },
        { path: "cta_subtitle", label: "CTA — subtitlu", type: "textarea" },
      ],
    };
  }),
];
